import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchSessionPolicy } from '@/lib/api/sessions';
import {
  DEFAULT_INACTIVITY_TIMEOUT_SECONDS,
  effectiveIdleMs,
  formatCountdown,
  idlePhase,
  idleThresholds,
  readSharedActivity,
  remainingMs,
  writeSharedActivity,
  type IdleThresholds,
} from '@/lib/session-idle';
import {
  consumeIntentionalSignOut,
  markIntentionalSignOut,
  recentSignOutReason,
  redirectToLogin,
  rememberSignOutReason,
} from '@/lib/session-signout';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// ============================================================================
// The open-tab half of the D-52 session standard (19A §6.5, 19H §0.7–0.8).
//
// GoTrue kills a session that has gone 60 minutes without a token refresh — but
// this app runs autoRefreshToken, so an open dormant tab refreshes itself and the
// server never sees it idle. THIS component is therefore the control for the open
// tab: no pointer / keyboard / visibility activity for (timeout − 2 min) → the
// warning modal with a live 2:00 countdown → a LOCAL sign-out at the timeout and
// a redirect carrying the "signed out due to inactivity" reason.
//
// It also owns the other way a session ends under a tab: the 24 h timebox, or a
// revoke from the Sessions card on another device, makes the next token refresh
// fail, supabase-js clears the session and emits SIGNED_OUT. A SIGNED_OUT nobody
// here asked for sends the tab to /login with "Please sign in again" — nothing
// else would, and the tab would sit on a dead session until a request 401s.
// Every deliberate sign-out in this app calls markIntentionalSignOut() first, so
// this listener can tell the two apart.
//
// The same behaviour as TPRM7's admin IdleSessionGuard (its PR #286), on this
// console's own plumbing (supabase client, apiRequest, Radix dialog).
//
// RULES THAT MATTER
//  * The timeout is the SERVER's policy (GET /users/me/sessions/policy), so an
//    operator's SESSION_INACTIVITY_TIMEOUT moves the warning with it; if it
//    cannot be fetched the 60-minute standard applies — never "off". A policy of
//    0 turns the detector off, as 0 does in GoTrue.
//  * Idle time is measured from the newest activity ANY tab saw (localStorage), or
//    a background tab nobody touched would sign the user out of the whole browser
//    session while they were busy in another tab. Activity also publishes there.
//  * Once the warning is up, stray pointer/keyboard activity does not dismiss it;
//    only "Stay signed in" does (or activity in another tab). Returning to a
//    hidden tab counts as activity only BEFORE the warning begins.
//  * The sign-out is `scope: 'local'` — it ends THIS session, not the user's
//    sessions on other devices. Wall-clock timestamps, not a decrementing timer,
//    so a slept laptop signs out on its first tick after waking.
// ============================================================================

const TICK_MS = 1000;
/** Activity is published at most this often (pointermove fires continuously). */
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

type Phase = 'off' | 'active' | 'warning';

export function IdleSessionGuard() {
  const [phase, setPhase] = useState<Phase>('off');
  const [remaining, setRemaining] = useState(0);

  const signedInRef = useRef(false);
  const phaseRef = useRef<Phase>('off');
  const thresholdsRef = useRef<IdleThresholds | null>(idleThresholds(DEFAULT_INACTIVITY_TIMEOUT_SECONDS));
  const lastActivityRef = useRef(Date.now());
  const lastPublishedRef = useRef(0);
  const signingOutRef = useRef(false);

  const setPhaseBoth = useCallback((p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  const markActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    lastPublishedRef.current = now;
    writeSharedActivity(now);
  }, []);

  // ── the idle sign-out: local scope, reason remembered for the other tabs ──
  const signOutForInactivity = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    rememberSignOutReason('inactivity');
    markIntentionalSignOut();
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) console.warn('[session] idle sign-out: server revoke failed; clearing locally', error.message);
    } catch (err) {
      // non-fatal: the browser's own session is what matters; the server session
      // ends on its inactivity timeout regardless. Redirect either way.
      console.warn('[session] idle sign-out: server revoke threw; clearing locally', err);
    }
    redirectToLogin('inactivity');
  }, []);

  const signOutNow = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    markIntentionalSignOut();
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[session] sign-out threw; redirecting anyway', err);
    }
    redirectToLogin(null);
  }, []);

  const stay = useCallback(() => {
    markActivity();
    setPhaseBoth('active');
  }, [markActivity, setPhaseBoth]);

  useEffect(() => {
    let cancelled = false;

    // Start tracking a signed-in session. Idempotent: getSession() and the
    // INITIAL_SESSION/SIGNED_IN events can all announce the same session.
    function startTracking() {
      if (signedInRef.current) return;
      signedInRef.current = true;
      signingOutRef.current = false;
      thresholdsRef.current = idleThresholds(DEFAULT_INACTIVITY_TIMEOUT_SECONDS);
      markActivity(); // a page load / sign-in is activity
      setPhaseBoth(thresholdsRef.current ? 'active' : 'off');
      void loadPolicy();
    }

    async function loadPolicy() {
      try {
        const { policy } = await fetchSessionPolicy();
        const secs = policy?.inactivity_timeout_seconds;
        if (cancelled || typeof secs !== 'number') return; // keep the 60-minute standard
        thresholdsRef.current = idleThresholds(secs);
        if (!thresholdsRef.current) setPhaseBoth('off');
        else if (phaseRef.current === 'off') setPhaseBoth('active');
      } catch (err) {
        // non-fatal: the 60-minute standard stays in force.
        console.warn('[session] could not read the session policy; using the 60-minute standard', err);
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data?.session) startTracking();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        startTracking();
        return;
      }
      // No session. Only a tab that WAS signed in has anything to do (the login
      // page's INITIAL_SESSION null is not an event).
      if (!signedInRef.current) return;
      signedInRef.current = false;
      setPhaseBoth('off');
      if (event !== 'SIGNED_OUT') return;
      if (consumeIntentionalSignOut()) return; // the initiator navigates itself
      redirectToLogin(recentSignOutReason() ?? 'expired');
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, [markActivity, setPhaseBoth]);

  // ── activity listeners ────────────────────────────────────────────────────
  useEffect(() => {
    function onActivity() {
      if (!signedInRef.current) return;
      // A warning that is up is dismissed only on purpose.
      if (phaseRef.current === 'warning') return;
      const now = Date.now();
      if (now - lastPublishedRef.current < ACTIVITY_THROTTLE_MS) return;
      markActivity();
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') onActivity();
    }
    const opts = { passive: true, capture: true } as const;
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, opts);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity, opts);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [markActivity]);

  // ── the clock: wall-clock arithmetic every second ─────────────────────────
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = thresholdsRef.current;
      if (!signedInRef.current || !t) return;
      const idle = effectiveIdleMs(Date.now(), lastActivityRef.current, readSharedActivity());
      const next = idlePhase(idle, t);
      if (next === 'expired') {
        void signOutForInactivity();
        return;
      }
      if (next === 'warning') {
        setRemaining(remainingMs(idle, t));
        if (phaseRef.current !== 'warning') setPhaseBoth('warning');
        return;
      }
      // 'active': also the path by which activity in another tab closes a warning.
      if (phaseRef.current !== 'active') setPhaseBoth('active');
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [setPhaseBoth, signOutForInactivity]);

  return (
    <Dialog
      open={phase === 'warning'}
      // Esc / the close × / an overlay click is the user interacting on purpose:
      // treat it as "Stay signed in".
      onOpenChange={(o) => {
        if (!o) stay();
      }}
    >
      <DialogContent style={{ maxWidth: 400 }}>
        <DialogHeader>
          <DialogTitle>Are you still there?</DialogTitle>
          {/* role=timer with live updates off: a screen reader reads the sentence once
              when the dialog opens (it is announced with its title), instead of being
              interrupted every second. */}
          <DialogDescription asChild>
            <p role="timer" aria-live="off" style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-primary)' }}>
              You&apos;ll be signed out in {formatCountdown(remaining)} due to inactivity.
            </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            type="button"
            onClick={() => void signOutNow()}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={stay}
            autoFocus
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stay signed in
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
